import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { ISODate } from '@app/shared'
import type { MailProposal, MailProposalKind } from '../../lib/data/types'
import type { DayItem, Section } from './controller'
import type { DayCtx } from './cards'
import { BellGlyph, CalendarGlyph, CheckGlyph, ClockGlyph, DocGlyph, External, MailGlyph, Shield, Sparkle } from './icons'
import { acceptLabel, acceptedWord, alreadyIn, canAccept, emptyLine, groupProposals, payloadLine, provenanceLines, whatHappens } from './proposals'

// ============================================================================
// THE NEXT STEPS PANEL (Lane 6b) — opens inline under a mail card, from the card's "Next steps"
// button or N. Plain and functional by owner decision (2026-10-07): existing tokens, the card's own
// button style, the existing undo toast. He redesigns it later; nothing here is a designed board.
// ============================================================================

const KIND_ICON: Record<MailProposalKind, (p: { size?: number; className?: string }) => ReactNode> = {
  calendar_block: CalendarGlyph,
  reminder: BellGlyph,
  waiting_on: ClockGlyph,
  open_link: External,
  mark_done: CheckGlyph,
  draft_reply: MailGlyph,
  verify_official: Shield,
  already_in_lifeos: DocGlyph,
}

export function StepsPanel({ item, section, index, ctx }: { item: DayItem; section: Section; index: number; ctx: DayCtx }) {
  const mail = item.mail
  if (!mail) return null
  const empty = emptyLine(mail)
  const { proposed, accepted, skipped } = groupProposals(mail.proposals ?? [])
  const prov = provenanceLines(mail.proposals ?? [], ctx.today)
  return (
    <div className="mt-2 rounded-button border border-line bg-surface" role="region" aria-label="Next steps">
      {empty ? (
        <p className="m-0 px-3 py-[10px] text-[12px] text-ink-3">{empty}</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {proposed.map((p) => (
            <Row key={p.id} p={p} today={ctx.today}>
              <span className="mt-[6px] flex flex-wrap gap-[6px]">
                {canAccept(p) && (
                  <button type="button" className="day-btn h-7" onClick={() => ctx.ctrl.acceptProposal(item, section, index, p)}>
                    {acceptLabel(p.kind)}
                  </button>
                )}
                <button type="button" className="day-btn h-7" onClick={() => ctx.ctrl.skipProposal(p)}>
                  Skip
                </button>
              </span>
            </Row>
          ))}
          {accepted.map((p) => (
            <Row key={p.id} p={p}>
              <span className="mt-[6px] flex flex-wrap items-center gap-[10px] text-[11.5px] font-medium text-ink-2">
                <span className="inline-flex items-center gap-1">
                  <CheckGlyph size={13} className="text-success" />
                  {acceptedWord(p.kind)}
                </span>
                {p.kind === 'draft_reply' && (
                  <a className="inline-flex items-center gap-1 text-accent" href={mail.url} target="_blank" rel="noreferrer">
                    Open in Gmail
                    <External size={12} />
                  </a>
                )}
              </span>
            </Row>
          ))}
          {skipped.map((p) => (
            <li key={p.id} className="border-t border-line-3 px-3 py-[7px] text-[11px] text-microcopy first:border-t-0">
              Skipped · {p.title}
            </li>
          ))}
        </ul>
      )}
      {prov.map((line) => (
        <p key={line} className="m-0 flex items-center gap-1 border-t border-line-3 px-3 py-[7px] text-[11px] leading-[1.4] text-microcopy">
          <Sparkle size={11} className="text-chat" />
          {line}
        </p>
      ))}
    </div>
  )
}

/** One proposal: kind icon, title, the evidence, what Accept would write, then its controls. A proposed row (today given) also says in one sentence what Accept will do. */
function Row({ p, today, children }: { p: MailProposal; today?: ISODate; children: ReactNode }) {
  const Icon = KIND_ICON[p.kind]
  // The payload's own words (the step to verify, why it is done) stay on the row; dates and places
  // now live inside the what-happens sentence.
  const extra = p.kind === 'verify_official' || p.kind === 'mark_done' ? payloadLine(p) : null
  const already = alreadyIn(p)
  const draft = p.kind === 'draft_reply' && 'text' in p.payload ? p.payload.text : null
  return (
    <li className="flex gap-[10px] border-t border-line-3 px-3 py-[9px] first:border-t-0" data-proposal={p.id} data-status={p.status}>
      <span className="mt-px text-ink-3">{Icon({ size: 14 })}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="text-[12px] font-semibold leading-[1.35] text-ink text-pretty">{p.title}</span>
        <span className="text-[11px] leading-[1.4] text-microcopy text-pretty">{p.detail}</span>
        {extra && <span className="text-[11.5px] leading-[1.4] text-ink-3 text-pretty">{extra}</span>}
        {already &&
          (already.href ? (
            <Link to={already.href} className="text-[11.5px] leading-[1.4] text-accent">
              {already.text}
            </Link>
          ) : (
            <span className="text-[11.5px] leading-[1.4] text-ink-3">{already.text}</span>
          ))}
        {today && <span className="text-[11.5px] leading-[1.4] text-ink-3 text-pretty">{whatHappens(p, today)}</span>}
        {draft && <span className="whitespace-pre-wrap rounded-button-sm bg-fill-2 px-2 py-[6px] text-[11.5px] leading-[1.45] text-ink-3">{draft}</span>}
        {children}
      </div>
    </li>
  )
}
